import { useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { config } from "@/shared/config";
import { Sentry } from "@/shared/lib/sentry";

export type AuthFetch = (path: string, init?: RequestInit) => Promise<Response>;

export function useAuthFetch(): AuthFetch {
  const { getAccessToken } = useAuth();
  return useCallback<AuthFetch>(
    async (path, init) => {
      const method = init?.method ?? "GET";
      try {
        const token = await getAccessToken();
        const body = init?.body;
        const isStructuredBody =
          body instanceof FormData ||
          body instanceof Blob ||
          body instanceof ArrayBuffer ||
          body instanceof URLSearchParams ||
          (typeof ReadableStream !== "undefined" && body instanceof ReadableStream);
        const res = await fetch(`${config.apiBaseUrl}${path}`, {
          ...init,
          headers: {
            Authorization: `Bearer ${token}`,
            ...(body && !isStructuredBody ? { "Content-Type": "application/json" } : {}),
            ...init?.headers,
          },
        });
        Sentry.addBreadcrumb({
          category: "fetch",
          type: "http",
          level: res.ok ? "info" : "warning",
          data: { method, url: path, status_code: res.status },
        });
        return res;
      } catch (error) {
        // getAccessToken can throw LoginRequiredError when refresh fails;
        // network failures on the fetch itself also land here. Capture so
        // these aren't invisible in prod.
        Sentry.captureException(error, {
          tags: { area: "auth-fetch" },
          extra: { method, path },
        });
        throw error;
      }
    },
    [getAccessToken],
  );
}
